import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  CopyButton,
  Group,
  NumberInput,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { ArrowsClockwiseIcon, CheckIcon, CopyIcon } from '@phosphor-icons/react';
import { type UuidVersion, generateUuids } from '@/lib/uuid';
import { useStrings } from '@/i18n/useLang';

export function UuidGenerator() {
  const t = useStrings();
  const [version, setVersion] = useState<UuidVersion>('v4');
  const [count, setCount] = useState(5);
  const [uppercase, setUppercase] = useState(false);
  const [hyphens, setHyphens] = useState(true);
  const [list, setList] = useState<string[]>([]);

  const generate = useCallback(() => {
    const n = Math.min(Math.max(Math.trunc(count) || 1, 1), 100);
    setList(generateUuids(version, n, { uppercase, hyphens }));
  }, [version, count, uppercase, hyphens]);

  useEffect(() => {
    generate();
  }, [generate]);

  const value = list.join('\n');

  return (
    <Stack gap="lg">
      <SegmentedControl
        fullWidth
        value={version}
        onChange={(v) => setVersion(v as UuidVersion)}
        data={[
          { value: 'v4', label: 'UUID v4' },
          { value: 'v7', label: 'UUID v7' },
        ]}
      />
      <Text size="sm" c="dimmed">
        {version === 'v4' ? t.ui.v4Hint : t.ui.v7Hint}
      </Text>

      <Group align="flex-end" wrap="wrap">
        <NumberInput
          label={t.ui.count}
          value={count}
          onChange={(v) => setCount(typeof v === 'number' ? v : 1)}
          min={1}
          max={100}
          w={120}
        />
        <Checkbox label={t.ui.uppercase} checked={uppercase} onChange={(e) => setUppercase(e.currentTarget.checked)} />
        <Checkbox label={t.ui.hyphens} checked={hyphens} onChange={(e) => setHyphens(e.currentTarget.checked)} />
      </Group>

      <Textarea
        label={t.ui.output}
        value={value}
        readOnly
        autosize
        minRows={3}
        maxRows={16}
        styles={{ input: { fontFamily: 'monospace' } }}
      />

      <Group>
        <Button leftSection={<ArrowsClockwiseIcon size={18} />} onClick={generate}>
          {t.ui.generate}
        </Button>
        <CopyButton value={value} timeout={1500}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? t.ui.copied : t.ui.copyAll} withArrow>
              <Button
                variant="default"
                leftSection={copied ? <CheckIcon size={18} /> : <CopyIcon size={18} />}
                onClick={copy}
              >
                {t.ui.copyAll}
              </Button>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
    </Stack>
  );
}
